// NAS カーソル取り込み（v1.4.0）— ルート 1 件分のカード。
// 既定は閉じた状態。開いたときだけ内訳を描く（DOM を有界に保つ）。
// 表示するのは相対プレフィックスと集計のみ。カーソル・本文・絶対パス・
// 内部URL・環境変数は一切扱わない（サーバー側の公開ビューにも含まれない）。
import { useState } from 'react';
import { CaretRight, CaretDown, CheckCircle, CircleNotch, Pause,
  WarningCircle, Warning, Clock, ArrowUUpLeft } from '@phosphor-icons/react';

const ROOT_STATE = {
  pending: { ja: '待機中', en: 'Pending', cls: 'text-zinc-500 bg-zinc-50 border-zinc-200', Icon: Clock },
  running: { ja: '実行中', en: 'Running', cls: 'text-sky-700 bg-sky-50 border-sky-200', Icon: CircleNotch, spin: true },
  completed: { ja: '完了', en: 'Completed', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle },
  paused: { ja: '一時停止', en: 'Paused', cls: 'text-amber-700 bg-amber-50 border-amber-200', Icon: Pause },
  truncated: { ja: '不完全', en: 'Incomplete', cls: 'text-amber-800 bg-amber-50 border-amber-300', Icon: WarningCircle },
  cancelled: { ja: '中止', en: 'Cancelled', cls: 'text-zinc-600 bg-zinc-50 border-zinc-300', Icon: Pause },
  failed: { ja: '失敗', en: 'Failed', cls: 'text-rose-700 bg-rose-50 border-rose-200', Icon: Warning },
};

const OUTCOME_JA = {
  indexed: ['登録', 'Indexed'],
  unchanged: ['変更なし', 'Unchanged'],
  unsupported_extension: ['対象外の拡張子', 'Unsupported type'],
  oversized: ['サイズ超過', 'Too large'],
  empty_file: ['空ファイル', 'Empty'],
  hidden_file: ['隠しファイル', 'Hidden'],
  guarded: ['機密フォルダー', 'Guarded'],
  privacy_excluded: ['プライバシー除外', 'Privacy excluded'],
  shortcut_or_link: ['ショートカット', 'Shortcut'],
  encrypted: ['パスワード保護', 'Password protected'],
  extractor_missing: ['抽出機能なし', 'No extractor'],
  extraction_failed: ['抽出失敗', 'Extraction failed'],
  no_text_extracted: ['本文なし', 'No text'],
  ocr_failed: ['OCR 失敗', 'OCR failed'],
  embedding_failed: ['埋め込み失敗', 'Embedding failed'],
  retrieval_transient_failure: ['取得の一時失敗', 'Transient retrieval failure'],
  retrieval_permanent_failure: ['取得失敗', 'Retrieval failed'],
};

// サーバー側 `nas_ingest.CONS_*` と同じ語彙。安定なら何も表示しない。
const CONSISTENCY_JA = {
  best_effort_stable: ['安定（最善努力）', 'Best-effort stable'],
  changed_during_enumeration: ['列挙中に変更あり', 'Changed during enumeration'],
  unverified: ['未検証', 'Not yet verified'],
};

const n = (v) => Number(v || 0).toLocaleString('ja-JP');
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('ja-JP') : '—');

export function NasIngestRootCard({ root, isCurrent, testIdPrefix = 'nas-ingest-root' }) {
  const [open, setOpen] = useState(false);          // 既定は閉じる
  const st = ROOT_STATE[root.status] || ROOT_STATE.pending;
  const Icon = st.Icon;
  const id = `${testIdPrefix}-${root.path}`;
  const outcomes = Object.entries(root.outcomes || {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className={`rounded-lg border ${isCurrent ? 'border-sky-300 ring-1 ring-sky-100' : 'border-zinc-200'} bg-white`}
      data-testid={id} data-status={root.status} data-complete={String(!!root.complete)}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
        data-testid={`${id}-toggle`}>
        {open ? <CaretDown size={12} className="shrink-0 text-zinc-400" />
          : <CaretRight size={12} className="shrink-0 text-zinc-400" />}
        {/* 狭い画面では件数と状態を次の行へ折り返し、名称を潰さない */}
        <span className="min-w-0 flex-1 basis-[calc(100%-2rem)] sm:basis-auto">
          <span className="block text-[12px] font-semibold text-zinc-800 truncate"
            title={root.label || root.path}>
            {root.label || root.path}
          </span>
          <span className="block text-[10.5px] font-mono text-zinc-400 truncate"
            title={root.path}>{root.path}</span>
        </span>
        <span className="shrink-0 ml-5 sm:ml-0 text-[10.5px] text-zinc-500 tabular-nums">
          {n(root.pages)}ページ / {n(root.files_observed)}件
        </span>
        <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${st.cls}`}
          data-testid={`${id}-state`}>
          <Icon size={10} weight="fill" className={st.spin ? 'animate-spin' : ''} />
          {st.ja}
        </span>
      </button>

      {open && (
        <div className="border-t border-zinc-100 px-3 py-2 space-y-2" data-testid={`${id}-body`}>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-[11px]">
            {[
              ['ページ処理数', 'Pages', n(root.pages)],
              ['検出', 'Observed', n(root.files_observed)],
              ['対象', 'Eligible', n(root.eligible_files)],
              ['処理済み', 'Processed', n(root.processed_files)],
              ['登録', 'Indexed', n(root.indexed_files)],
              ['変更なし', 'Unchanged', n(root.unchanged_files)],
              ['対象外', 'Skipped', n(root.skipped_files)],
              ['エラー', 'Failed', n(root.failed_files)],
              ['再試行', 'Retries', n(root.retries)],
              ['ページ再試行', 'Page retries', n(root.page_retries)],
              ['やり直したページ', 'Replayed pages', n(root.replayed_pages)],
              ['最終ページ取得', 'Last page', fmtDT(root.last_page_at)],
            ].map(([ja, en, val]) => (
              <div key={ja} className="min-w-0">
                <dt className="text-zinc-500 truncate">{ja}<span className="block text-[9.5px] text-zinc-400">{en}</span></dt>
                <dd className={`font-semibold tabular-nums ${ja === 'エラー' && Number(root.failed_files) > 0 ? 'text-rose-700' : 'text-zinc-800'}`}
                  data-testid={`${id}-${en.toLowerCase().replace(/ /g, '-')}`}>{val}</dd>
              </div>
            ))}
          </dl>

          {root.resumable && !root.complete && (
            <p className="flex items-center gap-1.5 text-[10.5px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1"
              data-testid={`${id}-resumable`}>
              <ArrowUUpLeft size={11} weight="bold" />
              続きの位置を保存しています（再開すればここから続きます）
              <span className="text-sky-700 opacity-80">/ Resume point saved</span>
            </p>
          )}

          {(root.truncated || root.typed_error) && (
            <p className="text-[10.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1"
              data-testid={`${id}-truncation`}>
              <Warning size={11} weight="fill" className="inline mr-1" />
              打ち切り / 分類:{' '}
              <span className="font-mono">{root.truncation_reason || root.typed_error}</span>
            </p>
          )}

          {root.consistency && root.consistency !== 'best_effort_stable' && (
            <p className="text-[10.5px] text-amber-900" data-testid={`${id}-consistency`}>
              整合性 / Consistency: {(CONSISTENCY_JA[root.consistency] || [root.consistency, ''])[0]}
              <span className="ml-1 text-zinc-500">
                {(CONSISTENCY_JA[root.consistency] || ['', ''])[1]}
              </span>
            </p>
          )}

          {outcomes.length > 0 && (
            <div data-testid={`${id}-outcomes`}>
              <p className="text-[10.5px] font-semibold text-zinc-500 mb-1">
                内訳 / Breakdown
              </p>
              <div className="flex flex-wrap gap-1">
                {outcomes.slice(0, 18).map(([k, v]) => {
                  const [ja, en] = OUTCOME_JA[k] || [k, k];
                  return (
                    <span key={k} title={en}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-zinc-200 bg-zinc-50 text-[10px] text-zinc-700"
                      data-testid={`${id}-outcome-${k}`}>
                      {ja}<span className="font-semibold tabular-nums">{n(v)}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NasIngestRootCard;
